import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ActionDialog from 'src/components/UI/dialog/AcitonDialog';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { clearWorkspace } from 'src/store/workspace/workspacePersistence';
import { UserContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const DESTROYS = [
  'Pedigrees and family records',
  'Clinical notes, tests and appointments',
  'Forms and submitted responses',
  'Clinics and scheduled sessions',
  'Pathways and patient boards'
];

export default function DemoReset() {
  const t = useAutoTranslation();
  const navigate = useNavigate();
  const { setUser } = React.useContext(UserContext);

  const [canReset, setCanReset] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    axios
      .get(APICONSTANTS.demoStatus)
      .then(({ data }) => active && setCanReset(Boolean(data?.canReset)))
      .catch(() => active && setCanReset(false));
    return () => {
      active = false;
    };
  }, []);

  const handleReset = async () => {
    setResetting(true);
    setError(null);
    try {
      await axios.post(APICONSTANTS.demoReset);
      await axios.get(APICONSTANTS.logout).catch(() => {});
      setUser(null);
      localStorage.setItem('2FAloggedin', 'false');
      // The reset destroys and reseeds the tenant's families, so every open
      // pedigree tab now names a family that no longer exists. This is the second
      // logout path in the app and the one where stale tabs are guaranteed
      // (TGCLOUD-1618).
      clearWorkspace();
      navigate(CONSTANTS.signinPath);
    } catch (err) {
      setError(
        err?.response?.data?.message || t('Reset failed. Nothing was reseeded.')
      );
      setResetting(false);
    }
  };

  if (!canReset) return null;

  return (
    <>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem
        onClick={() => setOpen(true)}
        sx={(theme) => ({
          color: theme.palette.error.main,
          fontWeight: 600,
          backgroundColor: alpha(theme.palette.error.main, 0.08),
          '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.16) }
        })}
      >
        {t(`Reset`)}
      </MenuItem>

      <ActionDialog
        title={t('Reset demo data')}
        open={open}
        handleClose={() => !resetting && setOpen(false)}
        onSubmit={handleReset}
        saveText={resetting ? t('Resetting') : t('Confirm reset')}
        submitColor="error"
        submitDisabled={resetting}
        minHeight={330}
      >
        <Box sx={{ pb: 7 }}>
          <Box
            sx={(theme) => ({
              border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
              backgroundColor: alpha(theme.palette.error.main, 0.06),
              borderRadius: 1.5,
              p: 2,
              mb: 2
            })}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}
            >
              {t('This deletes everything in the demo tenant')}
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.5,
                '& li': { mb: 0.25 },
                color: 'text.primary'
              }}
            >
              {DESTROYS.map((line) => (
                <Typography component="li" variant="body2" key={line}>
                  {t(line)}
                </Typography>
              ))}
            </Box>
          </Box>

          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t(
              'The seed script then rebuilds the original demo data. Anything the team added or edited is lost, and everyone signed in will be signed out.'
            )}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          {resetting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={18} color="error" />
              <Typography variant="body2" color="text.secondary">
                {t('This takes about 20 seconds. Keep this tab open.')}
              </Typography>
            </Box>
          )}
        </Box>
      </ActionDialog>
    </>
  );
}
